<script setup lang="ts">
import {
  IconActivity,
  IconAdjustments,
  IconAlertTriangle,
  IconBox,
  IconBriefcase,
  IconChartBar,
  IconChevronDown,
  IconDatabase,
  IconFileAnalytics,
  IconExternalLink,
  IconHome,
  IconLayoutGrid,
  IconLogout,
  IconMap2,
  IconMenu2,
  IconPlugConnected,
  IconSearch,
  IconServer,
  IconSettings,
  IconShieldLock,
  IconUsers,
  IconUsersGroup,
  IconX,
} from "@tabler/icons-vue";
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  type Component,
  type DeepReadonly,
} from "vue";
import { useRoute, useRouter } from "vue-router";

import type { SessionPayload } from "@/api/auth";
import { OMNIKPI_EXTERNAL_URL } from "@/appRegistry";
import { useSession } from "@/composables/session";
import { confirmExternalNavigation } from "@/ui/externalNavigation";
import BrandMark from "./BrandMark.vue";

type NavigationItem = {
  label: string;
  icon: Component;
  routeName?: string;
  hash?: string;
  externalUrl?: string;
  confirmBeforeOpen?: boolean;
  capability?: string;
  planned?: boolean;
};
type NavigationGroup = { label: string; items: NavigationItem[] };

const props = defineProps<{ session: DeepReadonly<SessionPayload> }>();
const route = useRoute();
const router = useRouter();
const { logout } = useSession();
const sidebarOpen = ref(false);
const profileOpen = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);

const selectedId = computed(
  () =>
    String(route.params.workspaceId ?? "") ||
    localStorage.getItem("omni.workspace") ||
    props.session.workspaces[0]?.id ||
    "",
);
const selected = computed(() =>
  props.session.workspaces.find(
    (workspace) => workspace.id === selectedId.value,
  ),
);
const search = computed({
  get: () => String(route.query.q ?? ""),
  set: (value: string) => {
    void router.replace({
      name: "hub",
      params: { workspaceId: selectedId.value },
      query: value.trim() ? { q: value } : {},
    });
  },
});
const activeLabel = computed(() => {
  const names: Record<string, string> = {
    hub: "Accueil",
    uploads: "Omni Upload",
    "upload-rejections": "Rejets d’import",
    inventory: "Network Inventory",
    catalogs: "Catalogs & Rules",
    "platform-artifacts": "Artifacts",
    "platform-jobs": "Jobs",
    "platform-activity": "Audit Logs",
    "mcp-tokens": "Accès MCP",
    operations: "Network Toolbox",
    "toolbox-tool": "Network Toolbox",
  };
  return names[String(route.name)] ?? "Omni Portal";
});
const accountLabel = computed(() => {
  const localPart = props.session.user?.email.split("@")[0] ?? "Compte";
  return localPart
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
});
const initials = computed(() =>
  accountLabel.value
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase(),
);

const navigation: NavigationGroup[] = [
  {
    label: "Opérations réseau",
    items: [
      { label: "Omni Upload", icon: IconBox, routeName: "uploads" },
      {
        label: "KPI Monitoring",
        icon: IconChartBar,
        externalUrl: OMNIKPI_EXTERNAL_URL,
        confirmBeforeOpen: true,
      },
      {
        label: "Network Inventory",
        icon: IconDatabase,
        routeName: "inventory",
        capability: "inventory.read",
      },
      {
        label: "Network Toolbox",
        icon: IconServer,
        routeName: "operations",
      },
      { label: "Parameter Audit", icon: IconFileAnalytics, planned: true },
      { label: "Capacity Analysis", icon: IconActivity, planned: true },
      { label: "GIS & Maps", icon: IconMap2, planned: true },
      { label: "Incidents & Alerts", icon: IconAlertTriangle, planned: true },
    ],
  },
  {
    label: "Plateforme data",
    items: [
      {
        label: "Catalogs & Rules",
        icon: IconLayoutGrid,
        routeName: "catalogs",
      },
      { label: "Jobs", icon: IconServer, routeName: "platform-jobs" },
      {
        label: "Artifacts",
        icon: IconBriefcase,
        routeName: "platform-artifacts",
      },
      {
        label: "Audit Logs",
        icon: IconShieldLock,
        routeName: "platform-activity",
      },
      {
        label: "Accès MCP",
        icon: IconPlugConnected,
        routeName: "mcp-tokens",
        capability: "mcp.read",
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        label: "Utilisateurs",
        icon: IconUsers,
        planned: true,
        capability: "admin.access",
      },
      {
        label: "Workspaces",
        icon: IconUsersGroup,
        planned: true,
        capability: "admin.access",
      },
      {
        label: "Rôles & accès",
        icon: IconShieldLock,
        planned: true,
        capability: "admin.access",
      },
      {
        label: "Paramètres",
        icon: IconSettings,
        planned: true,
        capability: "admin.access",
      },
      {
        label: "Django Admin",
        icon: IconAdjustments,
        externalUrl: "/admin/",
        capability: "admin.access",
      },
      {
        label: "Intégrations",
        icon: IconPlugConnected,
        planned: true,
        capability: "admin.access",
      },
    ],
  },
];

function canSee(item: NavigationItem) {
  return (
    !item.capability ||
    props.session.capabilities.includes(item.capability) ||
    selected.value?.capabilities.includes(item.capability)
  );
}

const visibleNavigation = computed<NavigationGroup[]>(() =>
  navigation
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => canSee(item)),
    }))
    .filter((group) => group.items.length > 0),
);

function routeFor(item: NavigationItem) {
  const target = {
    name: item.routeName!,
    params: { workspaceId: selectedId.value },
  };
  return item.hash ? { ...target, hash: item.hash } : target;
}

function isItemActive(item: NavigationItem) {
  if (
    item.routeName === "operations" &&
    !item.hash &&
    route.name === "toolbox-tool"
  )
    return true;
  if (route.name !== item.routeName) return false;
  return item.hash ? route.hash === item.hash : !route.hash;
}

function handleExternalNavigation(item: NavigationItem, event: MouseEvent) {
  if (item.confirmBeforeOpen) confirmExternalNavigation(event, "OmniKPI");
}

async function selectWorkspace(event: Event) {
  const id = (event.target as HTMLSelectElement).value;
  const name =
    typeof route.name === "string" &&
    [
      "hub",
      "catalogs",
      "uploads",
      "operations",
      "platform-artifacts",
      "platform-jobs",
      "platform-activity",
      "mcp-tokens",
    ].includes(route.name)
      ? route.name
      : "hub";
  localStorage.setItem("omni.workspace", id);
  await router.push({ name, params: { workspaceId: id } });
  sidebarOpen.value = false;
}

async function signOut() {
  await logout();
  await router.push({ name: "login" });
}

function handleShortcut(event: KeyboardEvent) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
    event.preventDefault();
    void nextTick(() => searchInput.value?.focus());
  }
  if (event.key === "Escape") {
    sidebarOpen.value = false;
    profileOpen.value = false;
  }
}

onMounted(() => window.addEventListener("keydown", handleShortcut));
onBeforeUnmount(() => window.removeEventListener("keydown", handleShortcut));
</script>

<template>
  <a class="skip-link" href="#main-content">Aller au contenu</a>
  <div class="portal-shell">
    <button
      v-if="sidebarOpen"
      class="sidebar-scrim"
      type="button"
      aria-label="Fermer la navigation"
      @click="sidebarOpen = false"
    />
    <aside
      class="sidebar"
      :class="{ 'sidebar--open': sidebarOpen }"
      aria-label="Navigation principale"
    >
      <div class="sidebar__brand-row">
        <RouterLink
          :to="{ name: 'hub', params: { workspaceId: selectedId } }"
          aria-label="Omni Portal — Accueil"
          @click="sidebarOpen = false"
        >
          <BrandMark />
        </RouterLink>
        <button
          class="sidebar__close"
          type="button"
          aria-label="Fermer la navigation"
          @click="sidebarOpen = false"
        >
          <IconX :size="21" />
        </button>
      </div>

      <nav class="sidebar__nav">
        <RouterLink
          class="sidebar__home"
          :to="{ name: 'hub', params: { workspaceId: selectedId } }"
          @click="sidebarOpen = false"
        >
          <IconHome :size="19" stroke-width="1.8" />
          <span>Accueil</span>
        </RouterLink>

        <section v-for="group in visibleNavigation" :key="group.label">
          <h2>{{ group.label }}</h2>
          <template v-for="item in group.items" :key="item.label">
            <RouterLink
              v-if="item.routeName"
              :class="{ 'item-active': isItemActive(item) }"
              :to="routeFor(item)"
              @click="sidebarOpen = false"
            >
              <component :is="item.icon" :size="18" stroke-width="1.75" />
              <span>{{ item.label }}</span>
            </RouterLink>
            <a
              v-else-if="item.externalUrl"
              :href="item.externalUrl"
              :target="
                item.externalUrl.startsWith('http') ? '_blank' : undefined
              "
              :rel="
                item.externalUrl.startsWith('http') ? 'noopener' : undefined
              "
              @click="handleExternalNavigation(item, $event)"
            >
              <component :is="item.icon" :size="18" stroke-width="1.75" />
              <span>{{ item.label }}</span>
              <IconExternalLink
                v-if="item.externalUrl.startsWith('http')"
                class="external-mark"
                :size="14"
              />
            </a>
            <span
              v-else-if="item.planned"
              class="sidebar__planned"
              :title="`${item.label} — bientôt disponible`"
            >
              <component :is="item.icon" :size="18" stroke-width="1.75" />
              <span>{{ item.label }}</span>
            </span>
          </template>
        </section>
      </nav>

      <div class="platform-card">
        <div>
          <IconBriefcase :size="18" />
          <p>
            <strong>Workspace actif</strong>
            {{ selected?.name ?? "Aucun workspace" }}
          </p>
        </div>
        <label for="workspace-select">Workspace</label>
        <select
          id="workspace-select"
          :value="selectedId"
          @change="selectWorkspace"
        >
          <option
            v-for="workspace in session.workspaces"
            :key="workspace.id"
            :value="workspace.id"
          >
            {{ workspace.name }}
          </option>
        </select>
      </div>
    </aside>

    <div class="portal-canvas">
      <header class="topbar">
        <div class="topbar__title">
          <button
            type="button"
            aria-label="Ouvrir la navigation"
            @click="sidebarOpen = true"
          >
            <IconMenu2 :size="23" />
          </button>
          <strong>{{ activeLabel }}</strong>
        </div>

        <label class="global-search">
          <span class="sr-only">Rechercher une application</span>
          <IconSearch :size="18" />
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            placeholder="Rechercher un outil, un jeu de données, un rapport…"
          />
          <kbd>Ctrl K</kbd>
        </label>

        <div class="topbar__actions">
          <div class="profile">
            <button
              class="profile__trigger"
              type="button"
              :aria-expanded="profileOpen"
              @click="profileOpen = !profileOpen"
            >
              <span class="profile__copy">
                <strong>{{ accountLabel }}</strong>
                <small>{{ selected?.role }} · {{ selected?.name }}</small>
              </span>
              <span class="profile__avatar">{{ initials }}</span>
              <IconChevronDown :size="16" />
            </button>
            <div v-if="profileOpen" class="profile__menu">
              <p>{{ session.user?.email }}</p>
              <button type="button" @click="signOut">
                <IconLogout :size="17" />Déconnexion
              </button>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" tabindex="-1"><slot /></main>
    </div>
  </div>
</template>

<style scoped>
.portal-shell {
  min-height: 100svh;
  background: var(--portal-bg);
}

.sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 40;
  display: flex;
  width: var(--sidebar-width);
  flex-direction: column;
  overflow-y: auto;
  color: #f5f8ff;
  background: linear-gradient(180deg, #022452 0%, #003b7a 100%);
  box-shadow: 10px 0 34px rgb(2 25 63 / 8%);
}

.sidebar__brand-row {
  display: flex;
  min-height: 4.8rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 1.15rem;
}

.sidebar__brand-row a {
  text-decoration: none;
}

.sidebar__close {
  display: none;
}

.sidebar__nav {
  display: grid;
  gap: 1.15rem;
  padding: 0 0.9rem 1rem;
}

.sidebar__nav section {
  display: grid;
  gap: 0.08rem;
}

.sidebar__nav h2 {
  margin: 0 0 0.38rem;
  padding: 0 0.38rem;
  color: #bcd1f3;
  font-size: 0.62rem;
  font-weight: 560;
  letter-spacing: 0.035em;
  text-transform: uppercase;
}

.sidebar__nav a,
.sidebar__planned {
  display: flex;
  min-height: 2rem;
  align-items: center;
  gap: 0.72rem;
  padding: 0.42rem 0.48rem;
  color: #eff5ff;
  font-size: 0.78rem;
  text-decoration: none;
  border-radius: 0.38rem;
  transition:
    background 140ms ease,
    color 140ms ease;
}

.sidebar__nav a:hover,
.sidebar__nav a.item-active {
  color: white;
  background: rgb(52 132 255 / 30%);
}

.sidebar__home.router-link-active {
  background: linear-gradient(100deg, #1673e7, #0f59c4);
  box-shadow: 0 0.35rem 1rem rgb(0 15 55 / 18%);
}

.sidebar__planned {
  color: #d3e0f5;
  cursor: default;
  opacity: 0.82;
}

.external-mark {
  margin-left: auto;
  font-size: 0.7rem;
}

.platform-card {
  display: grid;
  gap: 0.75rem;
  margin: auto 0.9rem 1rem;
  padding: 0.9rem;
  background: rgb(7 69 137 / 52%);
  border: 1px solid rgb(129 178 240 / 22%);
  border-radius: 0.55rem;
}

.platform-card > div {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}

.platform-card p {
  margin: 0;
  color: #d6e6ff;
  font-size: 0.68rem;
  line-height: 1.45;
}

.platform-card strong {
  display: block;
  color: white;
  font-weight: 650;
}

.platform-card label {
  color: #a9c5ec;
  font-size: 0.6rem;
  text-transform: uppercase;
}

.platform-card select {
  width: 100%;
  padding: 0.45rem 0.5rem;
  color: white;
  background: rgb(0 31 82 / 38%);
  border: 1px solid rgb(171 207 255 / 24%);
  border-radius: 0.35rem;
  font-size: 0.7rem;
}

.platform-card option {
  color: var(--ink);
}

.portal-canvas {
  min-height: 100svh;
  margin-left: var(--sidebar-width);
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 25;
  display: grid;
  height: var(--topbar-height);
  grid-template-columns: minmax(12rem, 0.5fr) minmax(20rem, 1.2fr) minmax(
      17rem,
      0.7fr
    );
  align-items: center;
  gap: 1.25rem;
  padding: 0 1.75rem;
  background: rgb(255 255 255 / 94%);
  border-bottom: 1px solid var(--line);
  backdrop-filter: blur(14px);
}

.topbar__title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.topbar__title button,
.icon-button,
.profile__trigger,
.profile__menu button,
.sidebar__close {
  color: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.topbar__title button {
  display: grid;
  padding: 0.2rem;
  place-items: center;
}

.topbar__title strong {
  font-size: 1.02rem;
}

.global-search {
  display: flex;
  height: 2.45rem;
  align-items: center;
  gap: 0.58rem;
  padding: 0 0.65rem;
  color: var(--muted);
  background: white;
  border: 1px solid #d7dfec;
  border-radius: 0.55rem;
  box-shadow: 0 0.18rem 0.7rem rgb(24 55 100 / 4%);
}

.global-search input {
  min-width: 0;
  flex: 1;
  color: var(--ink);
  background: transparent;
  border: 0;
  outline: 0;
  font-size: 0.76rem;
}

.global-search kbd {
  padding: 0.16rem 0.28rem;
  color: #506687;
  background: #f2f5fa;
  border: 1px solid #dce3ed;
  border-radius: 0.25rem;
  font: 0.56rem var(--font-mono);
}

.topbar__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.icon-button {
  position: relative;
  display: grid;
  width: 2rem;
  height: 2rem;
  place-items: center;
}

.profile {
  position: relative;
}

.profile__trigger {
  display: flex;
  align-items: center;
  gap: 0.58rem;
  text-align: right;
}

.profile__copy {
  display: grid;
}

.profile__copy strong {
  font-size: 0.72rem;
}

.profile__copy small {
  color: var(--muted);
  font-size: 0.6rem;
}

.profile__avatar {
  display: grid;
  width: 2.25rem;
  height: 2.25rem;
  place-items: center;
  color: #1767d8;
  background: #e5efff;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 650;
}

.profile__menu {
  position: absolute;
  top: calc(100% + 0.75rem);
  right: 0;
  width: 15rem;
  padding: 0.8rem;
  background: white;
  border: 1px solid var(--line);
  border-radius: 0.55rem;
  box-shadow: 0 1rem 2.5rem rgb(20 45 83 / 15%);
}

.profile__menu p {
  margin: 0 0 0.7rem;
  overflow: hidden;
  color: var(--muted);
  font-size: 0.7rem;
  text-overflow: ellipsis;
}

.profile__menu button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
}

main {
  min-height: calc(100svh - var(--topbar-height));
  outline: none;
}

.sidebar-scrim {
  display: none;
}

@media (max-width: 1120px) {
  .sidebar {
    transform: translateX(-100%);
    transition: transform 180ms ease;
  }

  .sidebar--open {
    transform: translateX(0);
  }

  .sidebar__close {
    display: grid;
    padding: 0.35rem;
    place-items: center;
    color: white;
  }

  .sidebar-scrim {
    position: fixed;
    inset: 0;
    z-index: 35;
    display: block;
    background: rgb(2 18 45 / 45%);
    border: 0;
    backdrop-filter: blur(2px);
  }

  .portal-canvas {
    margin-left: 0;
  }
}

@media (max-width: 760px) {
  .topbar {
    height: auto;
    min-height: 4.1rem;
    grid-template-columns: 1fr auto;
    gap: 0.55rem;
    padding: 0.7rem 1rem;
  }

  .global-search {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .profile__copy {
    display: none;
  }

  .global-search kbd {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar {
    transition: none;
  }
}
</style>
